/**
 * validator - form validation library, entry point.
 *
 * Usage:
 *   <form validate> ... </form>          // auto-attached on page load
 *   g.validator(form | selector)         // attach manually (dynamic forms)
 *   await g.validator.validate(form)     // -> boolean (errors are shown)
 *   g.validator.validateField(el)        // -> Promise<boolean>
 *   g.validator.register(name, def)      // add a rule at runtime
 *   g.init({ validator: { rules: { cnp: { message, validate } } } })
 *
 * Fields declare their rules as validate-* attributes on the value holder
 * (works with plain inputs and with selekt / date-selekt / file-selekt):
 *   <input name="email" validate-required validate-email
 *          validate-server="/validate/email" validate-server-attach="team">
 *
 * The form NEVER submits natively: on a clean validation the form emits
 * `validator:submit` and the app sends the data itself with g.fetch.
 * Errors render under the fields via g.fieldError (styles live in genus).
 * Full reference: docs/libs/validator.md.
 */
import { FormValidator, defaults } from './validator.js';
import { registry, register } from './rules.js';

// Bump on every meaningful change to the library.
const VERSION = '0.4.2';

// Custom rules can arrive before us via g.init({ validator: { rules } }) -
// pick them up whenever forms are attached.
function syncSettingsRules() {
	const custom = window.g && window.g.settings && window.g.settings.validator && window.g.settings.validator.rules;
	for (const name of Object.keys(custom || {})) register(name, custom[name]);
}

const resolveOne = (target) => (typeof target === 'string' ? document.querySelector(target) : target);

function validator(target) {
	syncSettingsRules();
	let els;
	if (typeof target === 'string') els = Array.from(document.querySelectorAll(target));
	else if (target instanceof Element) els = [target];
	else els = Array.from(target || []);
	const instances = els.map((form) => form.validator || new FormValidator(form));
	return instances.length === 1 ? instances[0] : instances;
}

validator.version = () => VERSION;
validator.defaults = defaults;
validator.register = register;
validator.rules = () => Object.keys(registry);
validator.get = (target) => {
	const el = resolveOne(target);
	return (el && el.validator) || null;
};
validator.validate = async (target) => {
	const form = resolveOne(target);
	if (!form) return false;
	const { valid } = await validator(form).validateAll();
	return valid;
};
// Validate only the fields inside a container (e.g. one wizard step). Returns
// { valid, invalid }; errors are shown like a normal validation.
validator.validateWithin = (container) => {
	const root = resolveOne(container);
	const form = root && root.closest('form'); // closest() includes root itself if it is the form
	if (!form) return Promise.resolve({ valid: true, invalid: [] });
	return validator(form).validateWithin(root);
};
validator.validateField = (target) => {
	const el = resolveOne(target);
	const form = el && (el.form || el.closest('form'));
	if (!form) return Promise.resolve(true);
	return validator(form).validateField(el);
};
validator.reset = (target) => {
	const inst = validator.get(resolveOne(target));
	if (inst) inst.reset();
};

function autoInit() {
	validator('form[validate]');
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', autoInit);
else autoInit();

if (window.g) {
	window.g.validator = validator;
	window.g.registerLib('validator');
} else {
	window.validator = validator;
}
